import type { components } from '@social-studio/api-client';

export type AssistantContext = components['schemas']['AssistantContextDto'];

const UUID = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';
const UUID_ONLY = new RegExp(`^${UUID}$`);
const MONTH = /^(\d{4}-(?:0[1-9]|1[0-2]))-\d{2}$/;

/** Pages that name one object, in the order they are tried: the page's first path segment decides the kind. */
const OBJECT_PAGES: ReadonlyArray<readonly [RegExp, AssistantContext['kind']]> = [
  [new RegExp(`^/content/(${UUID})(?:/|$)`), 'post'],
  [new RegExp(`^/review/(${UUID})$`), 'post'],
  [new RegExp(`^/campaigns/(${UUID})$`), 'campaign'],
  [new RegExp(`^/media/(${UUID})$`), 'media'],
];

/**
 * The page context the panel sends (spec 110 section 4.15 rule 5): the kind and IDs only, never a
 * title or text. The server validates the IDs with the member's rights and builds the label itself.
 */
export function contextForLocation(pathname: string, search: string): AssistantContext {
  for (const [pattern, kind] of OBJECT_PAGES) {
    const id = pattern.exec(pathname)?.[1];
    if (id) return { kind, id };
  }
  const params = new URLSearchParams(search);
  const uuidParam = (name: string): string | undefined => {
    const value = params.get(name)?.toLowerCase() ?? '';
    return UUID_ONLY.test(value) ? value : undefined;
  };
  if (pathname === '/inbox') {
    const id = uuidParam('message');
    return id ? { kind: 'inbox', id } : { kind: 'none' };
  }
  if (pathname === '/calendar') {
    const month = MONTH.exec(params.get('date') ?? '')?.[1];
    return month ? { kind: 'calendar', month } : { kind: 'calendar' };
  }
  if (pathname.startsWith('/planning')) {
    const property_id = uuidParam('property');
    return property_id ? { kind: 'planning', property_id } : { kind: 'planning' };
  }
  return { kind: 'none' };
}
